/* eslint-disable react/jsx-one-expression-per-line */
/* eslint-disable max-lines-per-function */
import { useHookstate, type State } from '@hookstate/core'
import { LoadingButton } from '@mui/lab'
import {
  Button,
  Collapse,
  Grid,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material'
import ClearIcon from '@mui/icons-material/Clear'
import React from 'react'
import FilterAutocompleteFetch from '~/components/FilterAutocompleteFetch'
import FilterSelectPeriod from '~/components/FilterAutocompletePeriod'
import { useLang } from '~/hooks/useLang'
import { buildReportTypeOptions } from './reportTypeOptions'
import FilterAutocompleteStations from '~/components/FilterAutocompleteStations'
import { UsersReportFilters } from '../interfaces'

interface AuditUsersReportFiltersProps {
  readonly hidden?: boolean
  readonly labelButton?: State<string>
  readonly loadingButton: State<boolean>
  readonly resetValues?: State<boolean>
  readonly focusChange?: State<boolean>
  readonly userId?: State<number | null>
  readonly initialPeriod: State<string>
  readonly onPeriodChange: (value: string) => void
  readonly onStartDateChange: (value: string | null) => void
  readonly onFinishDateChange: (value: string | null) => void
  readonly clearFilters: () => void
  readonly searchButton?: (filters?: UsersReportFilters) => void
  // props legadas mantidas como opcionais para compatibilidade, não utilizadas internamente
  readonly deviceId?: State<number | null>
  readonly action: State<string | null>
  readonly originType?: State<'users' | 'station' | null>
  readonly selectedDevice?: State<number | null>
}

export default function AuditUsersReportFilters({
  hidden,
  labelButton,
  loadingButton,
  userId,
  resetValues,
  focusChange,
  initialPeriod,
  onStartDateChange,
  onFinishDateChange,
  onPeriodChange,
  clearFilters,
  searchButton,
  // props legadas
  deviceId,
  action,
  originType,
  selectedDevice,
}: AuditUsersReportFiltersProps) {
  const { translate } = useLang()

  const reportTypeOptions = buildReportTypeOptions(translate)

  const [startDate, setStartDate] = React.useState<string | null>(null)
  const [finishDate, setFinishDate] = React.useState<string | null>(null)

  function applyFilters() {
    const filters: UsersReportFilters = {
      start_date: startDate ?? undefined,
      finish_date: finishDate ?? undefined,
      user_id: userId?.get() ?? undefined,
      action: action?.get() ?? undefined,
    }
    // remove undefined keys
    Object.keys(filters).forEach((key) => {
      if (filters[key as keyof UsersReportFilters] === undefined) delete filters[key as keyof UsersReportFilters]
    })
    searchButton?.(filters)
  }

  return (
    <Collapse in={hidden}>
      <Grid
        container
        xs={12}
        sx={{ width: '100%', display: 'flex', alignItems: 'center' }}
      >
        <Grid container direction={'column'} display={'flex'} gap={1}>
          <Grid container direction={'row'} flexWrap={'wrap'}>
            <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="action-label">
                  {translate('Actions')}
                </InputLabel>
                <Select
                  labelId="action-label"
                  id="action"
                  value={action.get() || ''}
                  label={translate('Actions')}
                  onChange={(e) => action.set(e.target.value)}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                        width: 200,
                      },
                    },
                  }}
                  sx={{
                    '& .MuiMenuItem-root': {
                      whiteSpace: 'normal',
                      wordWrap: 'break-word',
                    },
                  }}
                  endAdornment={
                    action.get() ? (
                      <IconButton
                        size="small"
                        onClick={() => action.set(null)}
                        sx={{ marginRight: 2 }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    ) : null
                  }
                >
                  {reportTypeOptions.map((option) => (
                    <MenuItem
                      key={option.value}
                      value={option.value}
                      sx={{
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                      }}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
              <FilterAutocompleteFetch
                label={translate('User')}
                urlFetch={'/user/get'}
                dataRequest={'user'}
                size={'small'}
                disabled={false}
                onFetchChange={(user: number | null) => userId?.set(user)}
                resetValues={resetValues?.get()}
                focusChange={focusChange}
                valueId={userId?.get() ? Number(userId?.get()) : undefined}
              />
            </Grid>



            <FilterSelectPeriod
              onStartDateChange={(value) => {
                const json = value?.toJSON() ?? null
                setStartDate(json)
                onStartDateChange(json)
              }}
              onFinishDateChange={(value) => {
                const json = value?.toJSON() ?? null
                setFinishDate(json)
                onFinishDateChange(json)
              }}
              onPeriodChange={onPeriodChange}
              period={initialPeriod.get()}
              xsDateBetween={2}
              xsPeriod={2}
              hidePeriod={false}
              marginDateBetween={0}
              disableFuture={false}
            />
          </Grid>
        </Grid>
      </Grid>

      <Grid
        container
        direction={'row'}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'end',
          pt: 4,
          gap: 2,
        }}
      >
        <Button
          variant="outlined"
          onClick={() => {
            clearFilters()
            deviceId?.set?.(null)
            originType?.set?.(null)
            selectedDevice?.set?.(null)
          }}
        >
          {translate('Clear Filters')}
        </Button>
        <LoadingButton
          loading={loadingButton.get()}
          variant="contained"
          onClick={applyFilters}
        >
          {labelButton?.get()}
        </LoadingButton>
      </Grid>
    </Collapse>
  )
}
